import { useEffect, useMemo, useState } from 'react'
import {
  applyTheme,
  getMsUntilNextAutoThemeChange,
  getStoredTheme,
  resolveTheme,
  setTheme,
  type ThemeMode,
} from '../theme'

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode | null>(() => getStoredTheme())
  const [autoTick, setAutoTick] = useState(0)

  const resolved = useMemo(() => resolveTheme(mode), [mode, autoTick])

  useEffect(() => {
    applyTheme(mode)
  }, [mode])

  useEffect(() => {
    if (mode !== null) return
    const ms = getMsUntilNextAutoThemeChange()
    const id = window.setTimeout(() => {
      setAutoTick((v) => v + 1)
      applyTheme(null)
    }, ms)
    return () => window.clearTimeout(id)
  }, [mode, autoTick])

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === 'pds_theme') setMode(getStoredTheme())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    if (mode !== 'system') return
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq) return
    const onChange = () => applyTheme('system')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [mode])

  return {
    mode,
    resolved,
    setMode: (next: ThemeMode) => {
      setMode(next)
      setTheme(next)
    },
    toggle: () => {
      const order: ThemeMode[] = ['light', 'dark', 'sunset', 'system']
      const current = mode ?? resolved
      const i = Math.max(0, order.indexOf(current === 'sunset' ? 'sunset' : current))
      const next: ThemeMode = order[(i + 1) % order.length] ?? 'light'
      setMode(next)
      setTheme(next)
    },
  }
}

